import { useEffect, useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import {
  PERSONAS,
  USE_CASES,
  GOALS,
  Persona,
  PersonaId,
  ONBOARDING_VERSION,
} from "../utils/personas";
import Button from "./ui/Button";

type Step = 0 | 1 | 2 | 3;

const STEP_LABELS = ["Welcome", "Your role", "How you'll use it", "Your goals"];

export default function Onboarding() {
  const { isSignedIn, user } = useUser();
  const navigate = useNavigate();

  const [show, setShow] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [persona, setPersona] = useState<PersonaId | null>(null);
  const [useCases, setUseCases] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    const meta = (user.unsafeMetadata || {}) as Record<string, unknown>;
    const done = meta.onboardingComplete === true && meta.onboardingVersion === ONBOARDING_VERSION;
    if (!done) {
      const t = setTimeout(() => setShow(true), 350);
      return () => clearTimeout(t);
    }
  }, [isSignedIn, user]);

  const selectedPersona = useMemo<Persona | undefined>(
    () => PERSONAS.find((p) => p.id === persona),
    [persona]
  );

  const personaUseCases = useMemo(
    () => (persona ? USE_CASES[persona] : []),
    [persona]
  );

  const canAdvance =
    (step === 0) ||
    (step === 1 && !!persona) ||
    (step === 2 && useCases.length > 0) ||
    (step === 3 && goals.length > 0);

  const next = () => {
    if (step < 3) setStep((s) => (s + 1) as Step);
    else void finish();
  };

  const back = () => {
    if (step > 0) setStep((s) => (s - 1) as Step);
  };

  const finish = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await user.update({
        unsafeMetadata: {
          ...(user.unsafeMetadata || {}),
          persona,
          useCases,
          goals,
          onboardingComplete: true,
          onboardingVersion: ONBOARDING_VERSION,
          completedAt: new Date().toISOString(),
        },
      });
      localStorage.setItem("whobrew_onboarded", "true");
      setShow(false);
      navigate("/my-card");
    } catch {
      setShow(false);
    } finally {
      setSaving(false);
    }
  };

  // Lets the user skip onboarding entirely. We still mark them as onboarded so
  // the wizard doesn't reappear next session, but we don't store any persona
  // answers. They can always pick a persona later via Profile.
  const skip = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await user.update({
        unsafeMetadata: {
          ...(user.unsafeMetadata || {}),
          onboardingComplete: true,
          onboardingVersion: ONBOARDING_VERSION,
          completedAt: new Date().toISOString(),
          skipped: true,
        },
      });
      localStorage.setItem("whobrew_onboarded", "true");
      setShow(false);
      navigate("/my-card");
    } catch {
      setShow(false);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (list: string[], setList: (v: string[]) => void, id: string) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-white" role="dialog" aria-modal="true" aria-label="Welcome to WhoBrew">
      <div className="absolute inset-0 bg-dot-grid opacity-50 pointer-events-none" />
      <div className="relative min-h-screen flex flex-col items-center justify-start sm:justify-center px-4 py-10 sm:py-12">
        <div className="w-full max-w-3xl">
          <header className="mb-6 sm:mb-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-lg font-serif font-bold text-coffee-900">WhoBrew</span>
              <span className="text-[10px] tracking-eyebrow text-coffee-500">Setup</span>
            </div>
            <div className="flex gap-1.5">
              {STEP_LABELS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i < step ? "w-6 bg-coffee-700" : i === step ? "w-10 bg-coffee-700" : "w-6 bg-coffee-100"
                  }`}
                />
              ))}
            </div>
          </header>

          <div className="bg-white rounded-2xl border border-coffee-100 shadow-coffee-lg overflow-hidden">
            <div className="p-6 sm:p-10">
              {step === 0 && <StepWelcome firstName={user?.firstName || ""} />}
              {step === 1 && (
                <StepPersona
                  persona={persona}
                  onPick={(id) => {
                    setPersona(id);
                    // Preselect the top 3 use cases for the chosen persona so the
                    // user only has to deselect rather than start from scratch.
                    const top = (USE_CASES[id] || []).slice(0, 3).map((u) => u.id);
                    setUseCases(top);
                  }}
                />
              )}
              {step === 2 && persona && (
                <StepUseCases
                  persona={selectedPersona!}
                  options={personaUseCases}
                  selected={useCases}
                  onToggle={(id) => toggle(useCases, setUseCases, id)}
                />
              )}
              {step === 3 && (
                <StepGoals selected={goals} onToggle={(id) => toggle(goals, setGoals, id)} />
              )}

              <div className="mt-8 sm:mt-10 flex items-center justify-between gap-3 flex-wrap">
                <Button
                  variant="ghost"
                  size="md"
                  onClick={back}
                  leadingIcon={<ArrowLeft className="w-4 h-4" />}
                  className={step === 0 ? "invisible" : ""}
                >
                  Back
                </Button>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-coffee-500">Step {step + 1} of 4</span>
                  <Button
                    onClick={next}
                    disabled={!canAdvance || saving}
                    loading={saving}
                    trailingIcon={!saving ? <ArrowRight className="w-4 h-4" /> : undefined}
                  >
                    {step === 3 ? (saving ? "Setting up" : "Finish setup") : "Continue"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 mt-6 text-xs text-coffee-500">
            <span>We use this to tailor your dashboard. Change it anytime in Profile.</span>
            <button
              onClick={skip}
              disabled={saving}
              className="text-coffee-600 hover:text-coffee-800 underline underline-offset-2 disabled:opacity-50"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- steps ---------------- */

function StepWelcome({ firstName }: { firstName: string }) {
  const greeting = firstName ? `Hi ${firstName}.` : "Welcome.";
  return (
    <div className="text-center sm:py-4">
      <div className="eyebrow mb-3">Setup · 4 quick steps</div>
      <h1 className="text-display text-4xl sm:text-5xl text-coffee-900">
        {greeting}<br />Let&apos;s set up your card.
      </h1>
      <p className="mt-5 text-coffee-600 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
        Four short questions. We'll preload your dashboard, link presets, and analytics around the
        work you actually do.
      </p>
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto">
        {["Tailored to your role", "60-second setup", "Change it anytime"].map((t) => (
          <div key={t} className="text-xs text-coffee-700 px-3 py-2.5 rounded-lg bg-coffee-50 border border-coffee-100">
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepPersona({
  persona,
  onPick,
}: {
  persona: PersonaId | null;
  onPick: (id: PersonaId) => void;
}) {
  return (
    <div>
      <div className="eyebrow mb-3">Step 1 of 4</div>
      <h2 className="text-display text-3xl sm:text-4xl text-coffee-900">What best describes you?</h2>
      <p className="mt-2 text-coffee-600 text-sm sm:text-base">Pick one. We'll surface the right link presets and dashboard sections.</p>
      <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PERSONAS.map((p) => {
          const active = persona === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onPick(p.id)}
              className={`relative text-left rounded-xl p-4 border transition-all ${
                active
                  ? "bg-coffee-50 border-coffee-600 ring-2 ring-coffee-600"
                  : "bg-white border-coffee-100 hover:bg-coffee-50/50 hover:border-coffee-300"
              }`}
              aria-pressed={active}
            >
              <div className="text-coffee-900 font-semibold text-sm">{p.label}</div>
              <div className="text-coffee-600 text-xs mt-0.5 leading-snug">{p.tagline}</div>
              {active && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-coffee-600 text-white flex items-center justify-center">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepUseCases({
  persona,
  options,
  selected,
  onToggle,
}: {
  persona: Persona;
  options: { id: string; label: string; description: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      <div className="eyebrow mb-3">Step 2 of 4 · {persona.label}</div>
      <h2 className="text-display text-3xl sm:text-4xl text-coffee-900">
        How will you use WhoBrew?
      </h2>
      <p className="mt-2 text-coffee-600 text-sm sm:text-base">
        Pick everything that applies — we'll preload your dashboard with the right links and tips.
      </p>

      <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((o) => {
          const active = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={`text-left rounded-xl p-4 border transition-all ${
                active
                  ? "bg-coffee-50 border-coffee-600 ring-2 ring-coffee-600"
                  : "bg-white border-coffee-100 hover:bg-coffee-50/50 hover:border-coffee-300"
              }`}
              aria-pressed={active}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                    active ? "bg-coffee-600 border-coffee-600" : "border-coffee-300"
                  }`}
                >
                  {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <div>
                  <div className="text-coffee-900 text-sm font-semibold">{o.label}</div>
                  <div className="text-coffee-600 text-xs mt-0.5 leading-snug">{o.description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="mt-5 text-xs text-coffee-500">
        {selected.length === 0 ? "Pick at least one to continue." : `${selected.length} selected`}
      </p>
    </div>
  );
}

function StepGoals({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      <div className="eyebrow mb-3">Step 3 of 4</div>
      <h2 className="text-display text-3xl sm:text-4xl text-coffee-900">What does success look like?</h2>
      <p className="mt-2 text-coffee-600 text-sm sm:text-base">Pick all that apply. We'll surface the right metrics and nudges.</p>

      <div className="mt-6 sm:mt-8 flex flex-wrap gap-2.5">
        {GOALS.map((g) => {
          const active = selected.includes(g.id);
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onToggle(g.id)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium border transition-all ${
                active
                  ? "bg-coffee-600 text-white border-coffee-600"
                  : "bg-white text-coffee-800 border-coffee-200 hover:bg-coffee-50 hover:border-coffee-400"
              }`}
              aria-pressed={active}
            >
              {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              <span>{g.label}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-5 text-xs text-coffee-500">
        {selected.length === 0 ? "Pick at least one to continue." : `${selected.length} selected`}
      </p>
    </div>
  );
}
